<template>
    <div class="support-notes-card">
        <div class="support-notes-header">
            <i class="mdi mdi-note-text-outline support-notes-icon" aria-hidden="true"></i>
            <span class="support-notes-label">{{ t('supportNotes.sectionHeader') }}</span>
            <span class="support-notes-count-pill">{{ total }}</span>
            <button type="button" class="support-notes-see-all" @click="openDrawer">{{ t('supportNotes.seeAll') }}</button>
        </div>

        <!-- Composer -->
        <div v-if="!composerExpanded" class="support-notes-composer-collapsed" @click="expandComposer">
            {{ t('supportNotes.composerCollapsed') }}
        </div>
        <div v-else class="support-notes-composer">
            <textarea ref="textareaRef" v-model="draftBody" class="support-notes-textarea" :placeholder="t('supportNotes.textareaPlaceholder')"></textarea>
            <div class="support-notes-category-chips">
                <button
                    v-for="cat in categories"
                    :key="cat.slug"
                    type="button"
                    class="support-notes-chip"
                    :class="{ 'support-notes-chip--active': draftCategory === cat.slug }"
                    @click="draftCategory = cat.slug"
                >
                    {{ cat.label }}
                </button>
            </div>
            <div class="support-notes-composer-actions">
                <button type="button" class="support-notes-cancel" @click="cancelComposer">{{ t('supportNotes.cancel') }}</button>
                <button type="button" class="support-notes-save" :class="{ 'support-notes-save--disabled': !canSave }" :disabled="!canSave" @click="save">
                    {{ t('supportNotes.save') }}
                </button>
            </div>
        </div>

        <!-- Recent notes -->
        <div class="support-notes-list">
            <NoteRow v-for="note in recentNotes" :key="note.id" :note="note" />
        </div>

        <!-- Footer -->
        <button v-if="total > 0" type="button" class="support-notes-footer-link" @click="openDrawer">
            {{ t('supportNotes.seeAllCount', { count: total }) }}
        </button>

        <SupportNotesDrawer v-model="drawerOpen" :account-id="accountId" :vessel-id="vesselId" :spa-name="spaName" @notes-changed="reload" />

        <div v-if="showError" class="support-notes-error-toast" role="alert">{{ errorMessage }}</div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick } from 'vue'
import NoteRow from './NoteRow.vue'
import SupportNotesDrawer from './SupportNotesDrawer.vue'
import { categoryLabel, loadCategories, noteCategories, sortNotes } from './categories'
import { t } from '@/i18n'
import { listNotes, createNote, type SupportNoteV1, type SupportNoteCategory, type CreateNoteBody, type ListNotesParams } from '@/api/supportNotes'

const props = defineProps<{
    accountId: number
    spaName: string
    // When provided, the card lists and creates notes scoped to this vessel.
    vesselId?: number | null
}>()

// Server-managed taxonomy, fetched once and shared via the categories module cache.
const categories = noteCategories()

const notes = ref<SupportNoteV1[]>([])
const total = ref(0)
const drawerOpen = ref(false)

const composerExpanded = ref(false)
const draftBody = ref('')
const draftCategory = ref<SupportNoteCategory>('general')
const textareaRef = ref<HTMLTextAreaElement | null>(null)

/** The default category for a new note: the first (lowest sortOrder) active category. */
function defaultCategory(): SupportNoteCategory {
    return categories.value[0]?.slug ?? 'general'
}

const showError = ref(false)
const errorMessage = ref('')

const recentNotes = computed(() => sortNotes(notes.value).slice(0, 3))
const canSave = computed(() => draftBody.value.trim().length > 0)

function reportError(message?: string) {
    errorMessage.value = message ?? t('supportNotes.errorToast')
    showError.value = true
    setTimeout(() => {
        showError.value = false
    }, 4000)
}

function openDrawer() {
    drawerOpen.value = true
}

function expandComposer() {
    composerExpanded.value = true
    nextTick(() => textareaRef.value?.focus())
}

function resetComposer() {
    composerExpanded.value = false
    draftBody.value = ''
    draftCategory.value = defaultCategory()
}

function cancelComposer() {
    resetComposer()
}

async function reload() {
    try {
        const listParams: ListNotesParams = { limit: 20 }
        if (props.vesselId != null) listParams.vesselId = props.vesselId
        const result = await listNotes(props.accountId, listParams)
        notes.value = sortNotes(result.notes)
        // When scoped to a vessel, the API's `total` reflects the whole account
        // rather than the vessel filter, so derive the count from the filtered
        // notes instead. Fall back to the server total for the account-wide view.
        total.value = props.vesselId != null ? result.notes.length : result.total
    } catch (error) {
        reportError(error instanceof Error ? error.message : undefined)
    }
}

async function save() {
    if (!canSave.value) return

    const body = draftBody.value.trim()
    const category = draftCategory.value

    // Optimistic insert at the top with a temporary note, then collapse + reset.
    const optimistic: SupportNoteV1 = {
        id: `optimistic-${Date.now()}`,
        accountId: props.accountId,
        vesselId: props.vesselId ?? null,
        authorId: '',
        authorName: '',
        authorInitials: '',
        category,
        categoryLabel: categoryLabel(category),
        body,
        pinned: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
    }
    const snapshot = notes.value
    const snapshotTotal = total.value
    notes.value = sortNotes([optimistic, ...notes.value])
    total.value = snapshotTotal + 1
    resetComposer()

    try {
        const createBody: CreateNoteBody = { category, body }
        if (props.vesselId != null) createBody.vesselId = props.vesselId
        const created = await createNote(props.accountId, createBody)
        // Swap the optimistic placeholder for the server note immediately so the UI
        // stays responsive...
        notes.value = sortNotes([created, ...snapshot])
        // ...then reconcile the list and the count pill against the authoritative
        // server total so `total` can't drift after an optimistic create.
        await reload()
    } catch (error) {
        // Roll back the optimistic change and surface the error.
        notes.value = snapshot
        total.value = snapshotTotal
        reportError(error instanceof Error ? error.message : undefined)
    }
}

onMounted(async () => {
    try {
        await loadCategories()
        // Seed the composer default once the taxonomy is known.
        draftCategory.value = defaultCategory()
    } catch {
        // Non-fatal: the category chips just stay empty; note listing still works.
    }
    await reload()
})
</script>

<style lang="scss" scoped>
.support-notes-card {
    background: #ffffff;
    border-radius: 8px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
    padding: 16px;
}

.support-notes-header {
    display: flex;
    align-items: center;
    gap: 8px;
}

.support-notes-icon {
    color: rgb(35, 94, 150);
    font-size: 18px;
}

.support-notes-label {
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgb(35, 94, 150);
}

.support-notes-count-pill {
    background: rgb(240, 240, 240);
    color: rgb(84, 84, 84);
    border-radius: 10px;
    padding: 1px 8px;
    font-size: 12px;
    font-weight: 600;
}

.support-notes-see-all {
    margin-left: auto;
    background: none;
    border: none;
    color: rgb(35, 94, 150);
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
}

.support-notes-composer-collapsed {
    margin-top: 12px;
    border: 1px dashed rgb(210, 219, 224);
    border-radius: 8px;
    padding: 12px;
    color: rgb(146, 148, 156);
    font-size: 14px;
    cursor: text;
}

.support-notes-composer {
    margin-top: 12px;
    border: 1px solid rgb(210, 219, 224);
    border-radius: 8px;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.support-notes-textarea {
    width: 100%;
    min-height: 72px;
    border: 1px solid rgb(210, 219, 224);
    border-radius: 8px;
    padding: 8px 10px;
    font-size: 14px;
    line-height: 1.5;
    resize: vertical;
    outline: none;

    &:focus {
        border-color: rgb(35, 94, 150);
    }
}

.support-notes-category-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.support-notes-chip {
    border: 1px solid rgb(210, 219, 224);
    background: #ffffff;
    color: rgb(84, 84, 84);
    border-radius: 14px;
    padding: 3px 12px;
    font-size: 12px;
    cursor: pointer;

    &--active {
        background: rgb(35, 94, 150);
        border-color: rgb(35, 94, 150);
        color: #ffffff;
    }
}

.support-notes-composer-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
}

.support-notes-cancel {
    background: none;
    border: none;
    color: rgb(146, 148, 156);
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    padding: 6px 12px;
}

.support-notes-save {
    background: rgb(35, 94, 150);
    color: #ffffff;
    border: none;
    border-radius: 8px;
    padding: 6px 16px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;

    &:active {
        background: rgb(31, 83, 132);
    }

    &--disabled {
        background: rgba(35, 94, 150, 0.4);
        cursor: default;
    }
}

.support-notes-list {
    margin-top: 8px;
}

.support-notes-footer-link {
    background: none;
    border: none;
    color: rgb(35, 94, 150);
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    padding: 12px 0 0;
}

.support-notes-error-toast {
    position: fixed;
    bottom: 24px;
    left: 50%;
    transform: translateX(-50%);
    background: rgb(176, 0, 32);
    color: #ffffff;
    padding: 10px 16px;
    border-radius: 8px;
    font-size: 13px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
    z-index: 2500;
}
</style>
